import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import EditIcon from '@atlaskit/icon/core/edit';
import { Box } from '@atlaskit/primitives';
import useHandleOutsideClick from '@databases/utils/hooks/useHandleOutsideClick';
import React, { useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl-next';

import { ActionComponent } from '../ActionComponent.tsx';
import { messages } from '../messages.ts';

import BulkEditValues, { type BulkEditValuesProps } from './BulkEditValues.tsx';

export type EditValueProps = {
  contentId: string;
  handleClearFields: () => void;
} & Pick<BulkEditValuesProps, 'initialValue' | 'selectedValues'>;

export const EditValues = ({
  contentId,
  handleClearFields,
  initialValue,
  selectedValues
}: EditValueProps) => {
  const { formatMessage } = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      const handleKeyDown = (e: KeyboardEvent) => {
        e.stopPropagation();
      };

      document.addEventListener('keydown', handleKeyDown, true);

      return () => {
        document.removeEventListener('keydown', handleKeyDown, true);
      };
    }
  }, [isOpen]);

  const togglePopup = () => {
    setIsOpen(!isOpen);
  };

  const closePopup = () => {
    if (isOpen) {
      setIsOpen(false);
    }
  };

  const excludedTargets = [
    '[id="bulk-edit-values"]',
    // Rich text formatting toolbar dropdown portals render outside the popover DOM.
    // Without these exclusions, clicking a toolbar dropdown closes the bulk edit.
    '[role="menu"]',
    '[role="listbox"]',
    '[role="dialog"]',
    '[data-placement]',
    '[data-testid="bulk-edit-date-calendar"]',
    '.menu-portal-target'
  ];

  useHandleOutsideClick(closePopup, popoverRef, excludedTargets);

  const EditValueTrigger = (props: any) => (
    <Box ref={props.ref}>
      <ActionComponent
        type="edit-values"
        selectionMode="value"
        isSelected={isOpen}
        message={formatMessage(messages['action-bar.edit-values'])}
        iconBefore={
          <EditIcon
            label={formatMessage(messages['action-bar.edit-values'])}
            size="medium"
            spacing="spacious"
          />
        }
        iconAfter={
          <ChevronDownIcon
            label={formatMessage(messages['action-bar.chevron.down.icon.label'])}
            size="small"
          />
        }
        onClick={togglePopup}
      />
    </Box>
  );

  return (
    <Box style={{ position: 'relative' }} ref={popoverRef}>
      <BulkEditValues
        contentId={contentId}
        initialValue={initialValue}
        isOpen={isOpen}
        offset={[0, 12]}
        placement="top-start"
        removeSelectedContent={handleClearFields}
        selectedValues={selectedValues}
        Trigger={EditValueTrigger}
      />
    </Box>
  );
};
